import { Box, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';

type InvitationStatusFilterValue =
  | 'pending'
  | 'clicked'
  | 'joined'
  | 'expired'
  | 'undeliverable'
  | 'all';

interface InvitationStatusFilterProps {
  value: string;
  onChange: (status: InvitationStatusFilterValue | undefined) => void;
}

export const InvitationStatusFilter: React.FC<InvitationStatusFilterProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [tempStatus, setTempStatus] = useState<string>(value || 'all');

  const statusOptions = [
    { value: 'all', label: t('invitations.filters.allStatuses') },
    { value: 'pending', label: t('invitations.status.pending') },
    { value: 'clicked', label: t('invitations.status.clicked') },
    { value: 'joined', label: t('invitations.status.joined') },
    { value: 'expired', label: t('invitations.status.expired') },
    { value: 'undeliverable', label: t('invitations.status.undeliverable') },
  ] as const;

  useEffect(() => {
    setTempStatus(value || 'all');
  }, [value]);

  const handleApply = () => {
    onChange(tempStatus as InvitationStatusFilterValue | undefined);
  };

  const handleClear = () => {
    setTempStatus('all');
    onChange('all');
  };

  const handleStatusSelect = (status: string) => {
    setTempStatus(status);
  };

  const isFilterActive = !!(value && value !== 'all');
  const selectedOption = statusOptions.find((option) => option.value === value);

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('invitations.filters.status')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>
            {selectedOption?.label || t('invitations.filters.allStatuses')}
          </FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="200px">
            <FilterButtonMenuHeader
              title={t('invitations.filters.status')}
              onClearAll={handleClear}
            />
            <Box p={3}>
              <VStack gap={0.5} align="stretch">
                {statusOptions.map((option) => (
                  <FilterMenuItem
                    key={option.value}
                    onClick={() => handleStatusSelect(option.value)}
                    isSelected={tempStatus === option.value}
                  >
                    <Text fontSize="sm">{option.label}</Text>
                  </FilterMenuItem>
                ))}
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
